import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';

/**
 * Event tracking utility for game analytics
 *
 * Events are logged to Firebase Firestore in the 'events' collection
 * for real-time analytics tracking beyond just leaderboard submissions
 */

export const EVENT_TYPES = {
  // Game events
  GAME_STARTED: 'game_started',
  GAME_COMPLETED: 'game_completed',
  GAME_ABANDONED: 'game_abandoned',

  // Card events
  CARD_FLIPPED: 'card_flipped',
  CARDS_MATCHED: 'cards_matched',
  CARDS_MISMATCHED: 'cards_mismatched',

  // Mode changes
  MODE_SELECTED: 'mode_selected',
  DIFFICULTY_SELECTED: 'difficulty_selected',

  // User actions
  LEADERBOARD_VIEWED: 'leaderboard_viewed',
  SCORE_SUBMITTED: 'score_submitted',
  SHARE_CLICKED: 'share_clicked',
  EMAIL_SIGNUP: 'email_signup',

  // Daily challenge
  DAILY_CHALLENGE_STARTED: 'daily_challenge_started',
  DAILY_CHALLENGE_COMPLETED: 'daily_challenge_completed',

  // Session events
  SESSION_STARTED: 'session_started',
  SESSION_ENDED: 'session_ended'
};

/**
 * Log an event to Firebase Analytics
 * @param {string} eventType - The type of event (use EVENT_TYPES constants)
 * @param {object} eventData - Additional data to log with the event
 */
export const logEvent = async (eventType, eventData = {}) => {
  // Only log in production or if explicitly enabled
  const enableTracking = process.env.REACT_APP_ENABLE_ANALYTICS !== 'false';

  if (!enableTracking && process.env.NODE_ENV !== 'production') {
    console.log('[Analytics Debug]', eventType, eventData);
    return;
  }

  try {
    const eventsRef = collection(db, 'events');

    await addDoc(eventsRef, {
      eventType,
      ...eventData,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight
    });
  } catch (error) {
    console.error('Failed to log event:', error);
  }
};

/**
 * Track game start
 */
export const trackGameStart = (mode, difficulty = null, isDailyChallenge = false) => {
  logEvent(EVENT_TYPES.GAME_STARTED, {
    mode,
    difficulty,
    isDailyChallenge
  });
};

/**
 * Track game completion
 * Also saves to 'games' collection for analytics dashboard
 */
export const trackGameComplete = async (mode, difficulty, time, moves, isDailyChallenge = false) => {
  // Log event to 'events' collection
  logEvent(EVENT_TYPES.GAME_COMPLETED, {
    mode,
    difficulty,
    completionTime: time,
    moveCount: moves,
    isDailyChallenge
  });

  // ALSO save to 'games' collection for analytics dashboard
  // This tracks ALL completed games, not just leaderboard submissions
  try {
    const gamesRef = collection(db, 'games');
    await addDoc(gamesRef, {
      mode,
      difficulty,
      time: Number(time.toFixed(6)),
      moves,
      isDailyChallenge,
      timestamp: serverTimestamp(),
      // Don't include playerName since game isn't submitted to leaderboard yet
      isLeaderboardSubmission: false
    });
  } catch (error) {
    console.error('Failed to save game completion:', error);
  }
};

/**
 * Track card flip
 */
export const trackCardFlip = (isMatch, moveCount) => {
  logEvent(isMatch ? EVENT_TYPES.CARDS_MATCHED : EVENT_TYPES.CARDS_MISMATCHED, {
    moveCount
  });
};

/**
 * Track score submission
 */
export const trackScoreSubmission = (playerName, mode, difficulty, time, moves) => {
  logEvent(EVENT_TYPES.SCORE_SUBMITTED, {
    playerName,
    mode,
    difficulty,
    time,
    moves
  });
};

/**
 * Track share action
 */
export const trackShare = (platform) => {
  logEvent(EVENT_TYPES.SHARE_CLICKED, {
    platform
  });
};

/**
 * Track email signup
 */
export const trackEmailSignup = (source) => {
  logEvent(EVENT_TYPES.EMAIL_SIGNUP, {
    source
  });
};

/**
 * Initialize session tracking
 */
export const initSessionTracking = () => {
  // Track session start
  logEvent(EVENT_TYPES.SESSION_STARTED);

  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    // Use sendBeacon for reliable tracking on page unload
    const sessionEndData = {
      eventType: EVENT_TYPES.SESSION_ENDED,
      timestamp: new Date().toISOString()
    };

    // Note: sendBeacon doesn't work with Firebase directly
    // This is a fallback - consider using Google Analytics for session tracking
    navigator.sendBeacon && navigator.sendBeacon(
      '/api/analytics',
      JSON.stringify(sessionEndData)
    );
  });
};

/**
 * Get user's session ID (stored in sessionStorage)
 */
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }

  return sessionId;
};

/**
 * Get or create user ID (stored in localStorage for cross-session tracking)
 */
export const getUserId = () => {
  let userId = localStorage.getItem('analytics_user_id');

  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('analytics_user_id', userId);
  }

  return userId;
};
